import { db } from '../config/firebase';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';

export interface AIStrategyConfig {
  id: string;
  name: string;
  type: 'rsi' | 'stochastic' | 'bollinger_bands' | 'supertrend';
  symbol: string;
  isActive: boolean;
  config: {
    // RSI Strategy
    period?: number;
    overbought?: number;
    oversold?: number;
    
    // Stochastic Strategy
    kPeriod?: number;
    dPeriod?: number;
    overboughtLevel?: number;
    oversoldLevel?: number;
    
    // Bollinger Bands Strategy
    multiplier?: number;
    
    // SuperTrend Strategy (Legacy - removed old parameters)
    // Old parameters: atrPeriod, atrMultiplier - no longer used with VidyaHeart Strategy 2
    
    // VidyaHeart Strategy 2 Configuration
    initialEntryLevel?: string;
    vidyaLength?: number;
    vidyaMomentum?: number;
    bandDistance?: number;
    
    // Common settings
    tradeAmountPercent?: number;
    quantity?: number;
    stopLossPercent?: number;
    takeProfitPercent?: number;
    
    // Threshold settings
    profitThreshold?: number;
    lossThreshold?: number;
    
    // Note: isActive flag is removed from config - we only use the top-level isActive flag
  };
  performance: {
    totalTrades: number;
    winRate: number;
    totalPnL: number;
    sharpeRatio: number;
    maxDrawdown: number;
  };
  riskSettings: {
    maxPositions: number;
    maxRiskPerTrade: number;
    stopLossPercent: number;
    takeProfitPercent: number;
  };
  createdAt: string;
  lastModified: string;
  lastActiveTime?: string;
  aiTradeStartEnabled?: boolean; // Whether AI trading is enabled for this strategy
}

export interface AIStrategyPersistenceData {
  userId: string;
  accountType: 'demo' | 'live';
  activeStrategies: {
    [symbol: string]: {
      strategyId: string;
      strategyName: string;
      strategyType: string;
      isActive: boolean;
      config: any;
      lastActivated: string;
    };
  };
  globalSettings: {
    aiTradingEnabled: boolean;
    maxConcurrentStrategies: number;
    riskManagementEnabled: boolean;
    autoStopLoss: boolean;
    autoTakeProfit: boolean;
  };
  lastUpdated: string;
}

export class AIStrategyPersistenceService {
  private static readonly COLLECTION_NAME = 'userDocuments';
  
  /**
   * Ensure aiTradingData structure exists for the account with new per-asset structure
   * @param userData - User document data
   * @param firestoreAccountType - Account type ('demo' or 'real')
   * @returns Updated aiTradingData structure
   */
  private static ensureAiTradingDataStructure(userData: any, firestoreAccountType: string): any {
    const accountData = userData[`${firestoreAccountType}AccountData`] || {};
    const existingAiTradingData = accountData.aiTradingData || {};
    
    const now = new Date().toISOString();
    
    // Return the structure with new per-asset format
    return {
      globalEnabled: existingAiTradingData.globalEnabled ?? existingAiTradingData.enabled ?? false,
      globalSettings: existingAiTradingData.globalSettings || {
        maxConcurrentAssets: 5,
        globalRiskLimit: 10,
        globalDailyLoss: 20,
        emergencyStopEnabled: true
      },
      assets: existingAiTradingData.assets || {},
      globalPerformance: existingAiTradingData.globalPerformance || {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        totalPnL: 0,
        averageTradeDuration: 0,
        sharpeRatio: 0,
        maxDrawdown: 0,
        activeAssets: []
      },
      lastUpdated: existingAiTradingData.lastUpdated || now,
      
      // Backward compatibility fields
      enabled: existingAiTradingData.enabled || false,
      strategies: existingAiTradingData.strategies || [],
      activeStrategies: existingAiTradingData.activeStrategies || [],
      performance: existingAiTradingData.performance || {
        totalTrades: 0,
        winningTrades: 0,
        losingTrades: 0,
        winRate: 0,
        totalPnL: 0,
        averageTradeDuration: 0,
        sharpeRatio: 0,
        maxDrawdown: 0
      },
      settings: existingAiTradingData.settings || {
        maxPositions: 5,
        maxRiskPerTrade: 2,
        maxDailyLoss: 10,
        autoStopLoss: true,
        autoTakeProfit: true,
        riskManagementEnabled: true
      }
    };
  }
  
  /**
   * Ensure asset-specific configuration exists
   * @param aiTradingData - AI trading data structure
   * @param symbol - Asset symbol
   * @returns Asset configuration
   */
  private static ensureAssetConfig(aiTradingData: any, symbol: string): any {
    if (!aiTradingData.assets[symbol]) {
      const now = new Date().toISOString();
      aiTradingData.assets[symbol] = {
        enabled: false,
        selectedStrategy: null,
        availableStrategies: [],
        activeStrategyInstance: null,
        performance: {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          totalPnL: 0,
          averageTradeDuration: 0,
          sharpeRatio: 0,
          maxDrawdown: 0
        },
        settings: {
          maxPositions: aiTradingData.globalSettings?.maxPositions || 1,
          maxRiskPerTrade: aiTradingData.globalSettings?.globalRiskLimit || 2,
          maxDailyLoss: aiTradingData.globalSettings?.globalDailyLoss || 10,
          autoStopLoss: true,
          autoTakeProfit: true,
          riskManagementEnabled: true
        },
        lastUpdated: now
      };
    }
    return aiTradingData.assets[symbol];
  }
  
  /**
   * Save active AI strategy configuration to Firestore
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   * @param strategyConfig - Strategy configuration
   */
  static async saveActiveStrategy(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string,
    strategyConfig: AIStrategyConfig
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      const userData = docSnap.exists() ? docSnap.data() : {};
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const existingAiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Get existing activeStrategies array
      const existingActiveStrategies = existingAiTradingData.activeStrategies;
      
      // Remove existing strategy for this symbol if it exists
      const filteredStrategies = existingActiveStrategies.filter((strategy: any) => strategy.symbol !== symbol);
      
      // Add new strategy with AI Trade Start enabled by default
      const newStrategy = {
        ...strategyConfig,
        aiTradeStartEnabled: true // Default to enabled when saving
      };
      
      const updatedStrategies = [...filteredStrategies, newStrategy];
      
      // Create updated aiTradingData structure
      const updatedAiTradingData = {
        ...existingAiTradingData,
        activeStrategies: updatedStrategies,
        enabled: updatedStrategies.length > 0, // Enable if there are active strategies
        lastUpdated: new Date().toISOString(),
        performance: existingAiTradingData.performance || {
          totalTrades: 0,
          winRate: 0,
          totalPnL: 0,
          sharpeRatio: 0,
          maxDrawdown: 0
        }
      };
      
      // Update the user document
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: updatedAiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      
    } catch (error) {
      console.error(`❌ Error saving AI strategy for ${symbol}:`, error);
      throw error;
    }
  }
  
  /**
   * Load active AI strategy configuration from Firestore
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param symbol - Trading symbol
   * @returns Active strategy configuration or null
   */
  static async loadActiveStrategy(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string
  ): Promise<AIStrategyConfig | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const userData = docSnap.data();
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      if (!aiTradingData.activeStrategies || aiTradingData.activeStrategies.length === 0) {
        return null;
      }
      
      // Find strategy for this symbol - look for strategies that are either active OR have AI trading enabled
      // console.log(`🔍 Searching for strategy ${symbol} in activeStrategies:`, {
      //   totalStrategies: aiTradingData.activeStrategies?.length || 0,
      //   strategies: aiTradingData.activeStrategies?.map((s: any) => ({
      //     symbol: s.symbol,
      //     isActive: s.isActive,
      //     aiTradeStartEnabled: s.aiTradeStartEnabled,
      //     name: s.name,
      //     configIsActive: s.config?.isActive
      //   })) || []
      // });
      
      const activeStrategy = aiTradingData.activeStrategies.find((strategy: any) => 
        strategy.symbol === symbol && (strategy.isActive || strategy.aiTradeStartEnabled)
      );
      
      if (!activeStrategy) {
        console.log(`❌ No active strategy found for ${symbol}`);
        return null;
      }
      
      // 🔥 SIMPLIFIED: Use only the top-level isActive flag
      const resolvedStrategy = {
        ...activeStrategy,
        // Use only the main isActive flag - no nested config.isActive
        isActive: activeStrategy.isActive,
        // Remove config.isActive - we only use the top-level flag
        config: {
          ...activeStrategy.config
          // No isActive flag here - we only use the top-level one
        }
      };
      
      // console.log(`✅ Found strategy for ${symbol}:`, {
      //   id: resolvedStrategy.id,
      //   name: resolvedStrategy.name,
      //   isActive: resolvedStrategy.isActive,
      //   aiTradeStartEnabled: resolvedStrategy.aiTradeStartEnabled
      // });
      
      // Return the resolved strategy config
      return resolvedStrategy as AIStrategyConfig;
      
    } catch (error) {
      console.error(`❌ Error loading AI strategy for ${symbol}:`, error);
      return null;
    }
  }
  
  /**
   * Load all active AI strategies for an account
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns All active strategies
   */
  static async loadAllActiveStrategies(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<AIStrategyConfig[]> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return [];
      }
      
      const userData = docSnap.data();
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      if (!aiTradingData.activeStrategies || aiTradingData.activeStrategies.length === 0) {
        return [];
      }
      
      // Filter active strategies - include strategies that are either active OR have AI trading enabled
      const activeStrategies = aiTradingData.activeStrategies.filter((strategy: any) => 
        strategy.isActive || strategy.aiTradeStartEnabled
      );
      
      return activeStrategies as AIStrategyConfig[];
      
    } catch (error) {
      console.error(`❌ Error loading all active AI strategies:`, error);
      return [];
    }
  }
  
  /**
   * Deactivate AI strategy for a symbol
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param symbol - Trading symbol
   */
  static async deactivateStrategy(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        return;
      }
      
      const userData = docSnap.data();
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const existingAiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      const existingActiveStrategies = existingAiTradingData.activeStrategies;
      
      // Remove strategy for this symbol
      const filteredStrategies = existingActiveStrategies.filter((strategy: any) => strategy.symbol !== symbol);
      
      // Create updated aiTradingData structure
      const updatedAiTradingData = {
        ...existingAiTradingData,
        activeStrategies: filteredStrategies,
        enabled: filteredStrategies.length > 0, // Disable if no active strategies
        lastUpdated: new Date().toISOString()
      };
      
      // Update the user document
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: updatedAiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      
    } catch (error) {
      console.error(`❌ Error deactivating AI strategy for ${symbol}:`, error);
      throw error;
    }
  }

  /**
   * Toggle AI Trade Start enabled/disabled for a specific asset
   * @param userId - User ID
   * @param accountType - Account type ('demo' | 'live')
   * @param symbol - Trading symbol
   * @param enabled - Whether AI Trade Start is enabled
   */
  static async toggleAITradeStart(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string,
    enabled: boolean
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = docSnap.data();
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const existingAiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      const existingActiveStrategies = existingAiTradingData.activeStrategies;
      
      // Find and update the strategy for this symbol
      const updatedStrategies = existingActiveStrategies.map((strategy: any) => {
        if (strategy.symbol === symbol) {
          return {
            ...strategy,
            aiTradeStartEnabled: enabled
          };
        }
        return strategy;
      });
      
      // Create updated aiTradingData structure
      const updatedAiTradingData = {
        ...existingAiTradingData,
        activeStrategies: updatedStrategies,
        lastUpdated: new Date().toISOString()
      };
      
      // Update the user document
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: updatedAiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      
    } catch (error) {
      console.error(`❌ Error toggling AI Trade Start for ${symbol}:`, error);
      throw error;
    }
  }
  
  /**
   * Update global AI trading settings
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param settings - Global settings
   */
  static async updateGlobalSettings(
    userId: string,
    accountType: 'demo' | 'live',
    settings: Partial<AIStrategyPersistenceData['globalSettings']>
  ): Promise<void> {
    try {
      const docId = `${userId}_${accountType}`;
      const docRef = doc(db, this.COLLECTION_NAME, docId);
      
      // Get existing data
      const docSnap = await getDoc(docRef);
      const existingData = docSnap.exists() ? docSnap.data() as AIStrategyPersistenceData : null;
      
      // Update global settings
      const globalSettings = {
        ...existingData?.globalSettings,
        ...settings
      };
      
      // Update the document
      await updateDoc(docRef, {
        globalSettings,
        lastUpdated: new Date().toISOString()
      });
      
      
    } catch (error) {
      console.error(`❌ Error updating global AI settings:`, error);
      throw error;
    }
  }
  
  /**
   * Get global AI trading settings
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns Global settings
   */
  static async getGlobalSettings(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<AIStrategyPersistenceData['globalSettings'] | null> {
    try {
      const docId = `${userId}_${accountType}`;
      const docRef = doc(db, this.COLLECTION_NAME, docId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      const data = docSnap.data() as AIStrategyPersistenceData;
      return data.globalSettings;
      
    } catch (error) {
      console.error(`❌ Error getting global AI settings:`, error);
      return null;
    }
  }
  
  /**
   * Check if AI trading is enabled for any symbol
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns True if any strategy is active
   */
  static async isAITradingActive(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<boolean> {
    try {
      const strategies = await this.loadAllActiveStrategies(userId, accountType);
      return strategies.length > 0;
      
    } catch (error) {
      console.error(`❌ Error checking AI trading status:`, error);
      return false;
    }
  }
  
  /**
   * Get all symbols with active AI strategies
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns Array of symbols with active strategies
   */
  static async getActiveSymbols(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<string[]> {
    try {
      const strategies = await this.loadAllActiveStrategies(userId, accountType);
      return strategies.map(strategy => strategy.symbol);
      
    } catch (error) {
      console.error(`❌ Error getting active symbols:`, error);
      return [];
    }
  }
  
  /**
   * Clear all AI strategy data for an account
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   */
  static async clearAllStrategies(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<void> {
    try {
      const docId = `${userId}_${accountType}`;
      const docRef = doc(db, this.COLLECTION_NAME, docId);
      
      await updateDoc(docRef, {
        activeStrategies: {},
        globalSettings: {
          aiTradingEnabled: false,
          maxConcurrentStrategies: 5,
          riskManagementEnabled: true,
          autoStopLoss: true,
          autoTakeProfit: true
        },
        lastUpdated: new Date().toISOString()
      });
      
      
    } catch (error) {
      console.error(`❌ Error clearing all AI strategies:`, error);
      throw error;
    }
  }
  
  /**
   * Update strategy performance metrics
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param symbol - Trading symbol
   * @param performance - Performance metrics
   */
  static async updateStrategyPerformance(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string,
    performance: Partial<AIStrategyConfig['performance']>
  ): Promise<void> {
    try {
      const docId = `${userId}_${accountType}`;
      const docRef = doc(db, this.COLLECTION_NAME, docId);
      
      // Get existing data
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        return;
      }
      
      const data = docSnap.data() as AIStrategyPersistenceData;
      const activeStrategy = data.activeStrategies[symbol];
      
      if (!activeStrategy) {
        return;
      }
      
      // Update performance metrics
      const updatedStrategy = {
        ...activeStrategy,
        performance: {
          totalTrades: 0,
          winRate: 0,
          totalPnL: 0,
          sharpeRatio: 0,
          maxDrawdown: 0,
          ...performance
        }
      };
      
      // Update the document
      await updateDoc(docRef, {
        [`activeStrategies.${symbol}`]: updatedStrategy,
        lastUpdated: new Date().toISOString()
      });
      
      
    } catch (error) {
      console.error(`❌ Error updating strategy performance for ${symbol}:`, error);
      throw error;
    }
  }

  // ==================== NEW PER-ASSET AI TRADING METHODS ====================

  /**
   * Enable or disable AI trading for a specific asset
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   * @param enabled - Whether to enable or disable AI trading for this asset
   */
  static async setAssetAITradingEnabled(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string,
    enabled: boolean
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      const userData = docSnap.exists() ? docSnap.data() : {};
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Ensure asset configuration exists
      const assetConfig = this.ensureAssetConfig(aiTradingData, symbol);
      
      // Update asset enabled state
      assetConfig.enabled = enabled;
      assetConfig.lastUpdated = new Date().toISOString();
      
      // Update strategy instance flags when enabling AI
      if (enabled && assetConfig.activeStrategyInstance) {
        assetConfig.activeStrategyInstance.isActive = true;
        assetConfig.activeStrategyInstance.aiTradeStartEnabled = true;
        assetConfig.activeStrategyInstance.enabled = true;
        assetConfig.activeStrategyInstance.lastModified = new Date().toISOString();
        
        // console.log(`🔥 Updated activeStrategyInstance for ${symbol}:`, {
        //   isActive: true,
        //   aiTradeStartEnabled: true,
        //   enabled: true
        // });
      } else if (!enabled && assetConfig.activeStrategyInstance) {
        assetConfig.activeStrategyInstance.isActive = false;
        assetConfig.activeStrategyInstance.aiTradeStartEnabled = false;
        assetConfig.activeStrategyInstance.enabled = false;
        assetConfig.activeStrategyInstance.lastModified = new Date().toISOString();
        
        // console.log(`🔥 Updated activeStrategyInstance for ${symbol}:`, {
        //   isActive: false,
        //   aiTradeStartEnabled: false,
        //   enabled: false
        // });
      }
      
      // 🔥 UPDATE activeStrategies ARRAY - Set isActive based on enabled state
      const activeStrategies = aiTradingData.activeStrategies || [];
      const strategyIndex = activeStrategies.findIndex((strategy: any) => 
        strategy.id && strategy.id.includes(symbol)
      );
      
      if (strategyIndex !== -1) {
        activeStrategies[strategyIndex].isActive = enabled;
        activeStrategies[strategyIndex].aiTradeStartEnabled = enabled;
        activeStrategies[strategyIndex].enabled = enabled;
        activeStrategies[strategyIndex].lastModified = new Date().toISOString();
        
        // console.log(`🔥 Updated activeStrategies[${strategyIndex}] for ${symbol}:`, {
        //   isActive: enabled,
        //   aiTradeStartEnabled: enabled,
        //   enabled: enabled
        // });
      } else {
        // console.log(`⚠️ No active strategy found for ${symbol} in activeStrategies array`);
      }
      
      // Update the activeStrategies array in aiTradingData
      aiTradingData.activeStrategies = activeStrategies;
      
      // Update global active assets list
      const activeAssets = aiTradingData.globalPerformance.activeAssets || [];
      if (enabled && !activeAssets.includes(symbol)) {
        activeAssets.push(symbol);
        // console.log(`➕ Added ${symbol} to activeAssets: [${activeAssets.join(', ')}]`);
      } else if (!enabled && activeAssets.includes(symbol)) {
        const index = activeAssets.indexOf(symbol);
        activeAssets.splice(index, 1);
        // console.log(`➖ Removed ${symbol} from activeAssets: [${activeAssets.join(', ')}]`);
      }
      aiTradingData.globalPerformance.activeAssets = activeAssets;
      
      // CRITICAL: Update global enabled state based on active assets
      const shouldGlobalBeEnabled = activeAssets.length > 0;
      if (aiTradingData.globalEnabled !== shouldGlobalBeEnabled) {
        // console.log(`🔧 CRITICAL: Setting globalEnabled=${shouldGlobalBeEnabled} (${activeAssets.length} active assets: ${activeAssets.join(', ')})`);
        aiTradingData.globalEnabled = shouldGlobalBeEnabled;
      }
      
      aiTradingData.lastUpdated = new Date().toISOString();
      
      // Update backward compatibility fields
      aiTradingData.enabled = aiTradingData.globalEnabled;
      
      // Update the user document
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: aiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      // console.log(`✅ AI Trading ${enabled ? 'enabled' : 'disabled'} for ${symbol}`);
    } catch (error) {
      console.error(`❌ Error updating AI trading state for ${symbol}:`, error);
      throw error;
    }
  }

  /**
   * COMPREHENSIVE STOP: Completely stop AI trading for an asset (sets ALL flags to false)
   * This method ensures ALL AI-related flags are set to false and saved to Firestore immediately
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live') 
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   */
  static async stopAssetAITrading(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string
  ): Promise<void> {
    // console.log(`🛑 COMPREHENSIVE STOP initiated for ${symbol}...`);
    
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      const userData = docSnap.exists() ? docSnap.data() : {};
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Ensure asset configuration exists
      const assetConfig = this.ensureAssetConfig(aiTradingData, symbol);
      
      // 🔥 CLOSE ALL OPEN TRADES FOR THIS SYMBOL
      // console.log(`🔒 Closing all open trades for ${symbol}...`);
      await this.closeAllOpenTradesForSymbol(userId, firestoreAccountType, symbol);
      
      // 🔥 SET ALL AI FLAGS TO FALSE
      // console.log(`🔧 Setting ALL AI flags to FALSE for ${symbol}:`);
      
      // Asset level flags
      assetConfig.enabled = false;
      // console.log(`   ✅ assetConfig.enabled = false`);
      
      // Strategy instance flags - Set ALL to false
      if (assetConfig.activeStrategyInstance) {
        assetConfig.activeStrategyInstance.isActive = false;
        assetConfig.activeStrategyInstance.aiTradeStartEnabled = false;
        assetConfig.activeStrategyInstance.enabled = false;
        assetConfig.activeStrategyInstance.lastModified = new Date().toISOString();
        // console.log(`   ✅ activeStrategyInstance.isActive = false`);
        // console.log(`   ✅ activeStrategyInstance.aiTradeStartEnabled = false`);
        // console.log(`   ✅ activeStrategyInstance.enabled = false`);
      }
      
      // 🔥 UPDATE activeStrategies ARRAY - Set isActive to false for this symbol
      const activeStrategies = aiTradingData.activeStrategies || [];
      const strategyIndex = activeStrategies.findIndex((strategy: any) => 
        strategy.id && strategy.id.includes(symbol)
      );
      
      if (strategyIndex !== -1) {
        activeStrategies[strategyIndex].isActive = false;
        activeStrategies[strategyIndex].aiTradeStartEnabled = false;
        activeStrategies[strategyIndex].enabled = false;
        activeStrategies[strategyIndex].lastModified = new Date().toISOString();
        // console.log(`   ✅ activeStrategies[${strategyIndex}].isActive = false for ${symbol}`);
        // console.log(`   ✅ activeStrategies[${strategyIndex}].aiTradeStartEnabled = false`);
        // console.log(`   ✅ activeStrategies[${strategyIndex}].enabled = false`);
      } else {
        // console.log(`   ⚠️ No active strategy found for ${symbol} in activeStrategies array`);
      }
      
      // Update the activeStrategies array in aiTradingData
      aiTradingData.activeStrategies = activeStrategies;
      
      // Update timestamps
      assetConfig.lastUpdated = new Date().toISOString();
      
      // Remove from global active assets list
      const activeAssets = aiTradingData.globalPerformance.activeAssets || [];
      const assetIndex = activeAssets.indexOf(symbol);
      if (assetIndex !== -1) {
        activeAssets.splice(assetIndex, 1);
        // console.log(`   ✅ Removed ${symbol} from activeAssets: [${activeAssets.join(', ')}]`);
      }
      aiTradingData.globalPerformance.activeAssets = activeAssets;
      
      // Update global flags based on remaining active assets
      const shouldGlobalBeEnabled = activeAssets.length > 0;
      if (aiTradingData.globalEnabled !== shouldGlobalBeEnabled) {
        aiTradingData.globalEnabled = shouldGlobalBeEnabled;
        // console.log(`   ✅ globalEnabled = ${shouldGlobalBeEnabled} (${activeAssets.length} remaining active assets)`);
      }
      
      // Update timestamps
      aiTradingData.lastUpdated = new Date().toISOString();
      
      // Backward compatibility - ensure legacy flags are also false
      if (!shouldGlobalBeEnabled) {
        aiTradingData.enabled = false;
        // console.log(`   ✅ Legacy enabled flag = false`);
      }
      
      // 🔥 SAVE TO FIRESTORE IMMEDIATELY - NO DELAYS
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: aiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      // console.log(`✅ COMPREHENSIVE STOP COMPLETE: All AI flags saved to Firestore for ${symbol}`);
      // console.log(`📊 Final state: enabled=false, isActive=false, aiTradeStartEnabled=false, globalEnabled=${shouldGlobalBeEnabled}`);
      
    } catch (error) {
      console.error(`❌ Error in comprehensive stop for ${symbol}:`, error);
      throw error;
    }
  }

  /**
   * Close all open trades for a specific symbol and release margin back to balance
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   */
  private static async closeAllOpenTradesForSymbol(
    userId: string,
    accountType: string,
    symbol: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        // console.log(`No user document found for ${userId}`);
        return;
      }
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc[`${accountType}AccountData`];
      
      if (!accountData) {
        // console.log(`No account data found for ${accountType}`);
        return;
      }
      
      const openPositions = accountData.openPositions || [];
      const openOrders = accountData.openOrders || [];
      
      // Find all positions and orders for this symbol
      const symbolPositions = openPositions.filter((pos: any) => pos.symbol === symbol);
      const symbolOrders = openOrders.filter((order: any) => order.symbol === symbol);
      
      // console.log(`Found ${symbolPositions.length} positions and ${symbolOrders.length} orders for ${symbol}`);
      
      // Close all positions for this symbol
      for (const position of symbolPositions) {
        try {
          await this.closePositionAndReleaseMargin(userId, accountType, position);
          // console.log(`✅ Closed position ${position.id} for ${symbol}`);
        } catch (error) {
          console.error(`❌ Failed to close position ${position.id}:`, error);
        }
      }
      
      // Cancel all orders for this symbol
      for (const order of symbolOrders) {
        try {
          await this.cancelOrderAndReleaseMargin(userId, accountType, order);
          // console.log(`✅ Cancelled order ${order.id} for ${symbol}`);
        } catch (error) {
          console.error(`❌ Failed to cancel order ${order.id}:`, error);
        }
      }
      
      // console.log(`✅ All trades closed and margin released for ${symbol}`);
      
    } catch (error) {
      console.error(`❌ Error closing trades for ${symbol}:`, error);
      throw error;
    }
  }

  /**
   * Close a position and release margin back to balance
   * @param userId - User ID
   * @param accountType - Account type
   * @param position - Position to close
   */
  private static async closePositionAndReleaseMargin(
    userId: string,
    accountType: string,
    position: any
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return;
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc[`${accountType}AccountData`];
      
      if (!accountData) return;
      
      // Calculate P&L and margin release
      const entryPrice = parseFloat(position.entry);
      const closePrice = parseFloat(position.current || position.entry); // Use current price or entry price
      const size = parseFloat(position.size);
      const leverage = position.leverage || 100;
      
      // Calculate P&L
      let numericPnL: number;
      if (position.type === 'Buy') {
        numericPnL = (closePrice - entryPrice) * size;
      } else {
        numericPnL = (entryPrice - closePrice) * size;
      }
      
      // Calculate margin that was used for this position
      const marginUsed = (entryPrice * size) / leverage;
      
      // Create closed trade record
      const closedTrade = {
        id: position.id,
        symbol: position.symbol,
        type: position.type,
        size: position.size,
        entry: position.entry,
        close: closePrice.toString(),
        pnl: numericPnL,
        pnlPercent: (numericPnL / marginUsed) * 100,
        leverage: leverage,
        timestamp: new Date().toISOString(),
        isAI: position.isAI || false,
        closedBy: 'AI_STOP'
      };
      
      // Remove from open positions
      const updatedOpenPositions = accountData.openPositions.filter((p: any) => p.id !== position.id);
      
      // Add to closed trades
      const closedTrades = accountData.closedTrades || [];
      closedTrades.push(closedTrade);
      
      // Update balance (release margin + add P&L)
      const currentBalance = accountData.balance || 0;
      const newBalance = currentBalance + marginUsed + numericPnL;
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date().toISOString();
      
      // Save to Firestore
      await updateDoc(docRef, {
        [`${accountType}AccountData.openPositions`]: updatedOpenPositions,
        [`${accountType}AccountData.closedTrades`]: closedTrades,
        [`${accountType}AccountData.balance`]: newBalance,
        [`${accountType}AccountData.accountMetadata.lastActive`]: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });
      
      // console.log(`💰 Released ${marginUsed} margin and ${numericPnL >= 0 ? 'added' : 'subtracted'} ${Math.abs(numericPnL)} P&L for ${position.symbol}`);
      
    } catch (error) {
      console.error(`❌ Error closing position ${position.id}:`, error);
      throw error;
    }
  }

  /**
   * Cancel an order and release margin back to balance
   * @param userId - User ID
   * @param accountType - Account type
   * @param order - Order to cancel
   */
  private static async cancelOrderAndReleaseMargin(
    userId: string,
    accountType: string,
    order: any
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) return;
      
      const userDoc = docSnap.data() as any;
      const accountData = userDoc[`${accountType}AccountData`];
      
      if (!accountData) return;
      
      // Calculate margin that was used for this order
      const price = parseFloat(order.price || order.stopPrice);
      const size = parseFloat(order.size);
      const leverage = order.leverage || 100;
      const marginUsed = (price * size) / leverage;
      
      // Remove from open orders
      const updatedOpenOrders = accountData.openOrders.filter((o: any) => o.id !== order.id);
      
      // Update balance (release margin)
      const currentBalance = accountData.balance || 0;
      const newBalance = currentBalance + marginUsed;
      
      // Update account metadata
      accountData.accountMetadata.lastActive = new Date().toISOString();
      
      // Save to Firestore
      await updateDoc(docRef, {
        [`${accountType}AccountData.openOrders`]: updatedOpenOrders,
        [`${accountType}AccountData.balance`]: newBalance,
        [`${accountType}AccountData.accountMetadata.lastActive`]: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });
      
      //  console.log(`💰 Released ${marginUsed} margin for cancelled order ${order.id}`);
      
    } catch (error) {
      console.error(`❌ Error cancelling order ${order.id}:`, error);
      throw error;
    }
  }

  /**
   * Save active AI strategy configuration to Firestore for a specific asset
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   * @param strategyConfig - Strategy configuration
   */
  static async saveAssetStrategy(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string,
    strategyConfig: AIStrategyConfig
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      const userData = docSnap.exists() ? docSnap.data() : {};
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Ensure asset configuration exists
      const assetConfig = this.ensureAssetConfig(aiTradingData, symbol);
      
      // 🔥 SIMPLIFIED: Create strategy instance with single isActive flag
      const strategyInstance = {
        ...strategyConfig,
        id: `${symbol}_${Date.now()}`,
        symbol: symbol,
        // When saving a strategy, it should be ready to start but not yet active
        aiTradeStartEnabled: true, // Strategy is saved and ready to start
        isActive: false, // Strategy is saved but not currently running
        createdAt: new Date().toISOString(),
        // Remove config.isActive - we only use the top-level isActive flag
        config: {
          ...strategyConfig.config
          // No isActive flag here - we only use the top-level one
        }
      };
      
      // Update asset configuration
      assetConfig.enabled = true;  // ✅ Enable AI trading for this asset
      assetConfig.selectedStrategy = strategyInstance.id;
      assetConfig.activeStrategyInstance = strategyInstance;
      assetConfig.lastUpdated = new Date().toISOString();
      
      // Add to available strategies if not already there
      const existingStrategyIndex = assetConfig.availableStrategies.findIndex(
        (s: any) => s.name === strategyConfig.name
      );
      if (existingStrategyIndex >= 0) {
        assetConfig.availableStrategies[existingStrategyIndex] = strategyInstance;
      } else {
        assetConfig.availableStrategies.push(strategyInstance);
      }
      
      aiTradingData.lastUpdated = new Date().toISOString();
      
      // Update backward compatibility fields
      const filteredStrategies = aiTradingData.activeStrategies.filter((s: any) => s.symbol !== symbol);
      aiTradingData.activeStrategies = [...filteredStrategies, strategyInstance];
      
      // console.log(`💾 Saving strategy for ${symbol}:`, {
      //   strategyInstance: {
      //     id: strategyInstance.id,
      //     name: strategyInstance.name,
      //     symbol: strategyInstance.symbol,
      //     isActive: strategyInstance.isActive,
      //     aiTradeStartEnabled: strategyInstance.aiTradeStartEnabled,
      //     config: strategyInstance.config
      //   },
      //   activeStrategiesCount: aiTradingData.activeStrategies.length
      // });
      
      // Update the user document
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: aiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      // console.log(`✅ Strategy saved successfully for ${symbol}`);
      
    } catch (error) {
      console.error('❌ Error saving asset strategy:', error);
      throw error;
    }
  }

  /**
   * Get AI trading configuration for a specific asset
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   * @returns Asset AI trading configuration
   */
  static async getAssetAIConfig(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string
  ): Promise<any | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        return null;
      }
      
      const userData = docSnap.data();
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Return asset configuration or null if not configured
      return aiTradingData.assets[symbol] || null;
    } catch (error) {
      console.error(`❌ Error loading AI config for ${symbol}:`, error);
      return null;
    }
  }

  /**
   * Get all active assets with AI trading enabled
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   * @returns Array of asset configurations
   */
  static async getAllActiveAssets(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<Array<{ symbol: string; config: any }>> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        return [];
      }
      
      const userData = docSnap.data();
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Filter and return active assets
      const activeAssets: Array<{ symbol: string; config: any }> = [];
      
      for (const [symbol, config] of Object.entries(aiTradingData.assets)) {
        if ((config as any).enabled) {
          activeAssets.push({ symbol, config });
        }
      }
      
      return activeAssets;
    } catch (error) {
      console.error('❌ Error loading active assets:', error);
      return [];
    }
  }

  /**
   * Update asset-specific AI trading settings
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   * @param settings - Asset-specific settings
   */
  static async updateAssetSettings(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string,
    settings: Partial<any>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      const userData = docSnap.exists() ? docSnap.data() : {};
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // Ensure asset configuration exists
      const assetConfig = this.ensureAssetConfig(aiTradingData, symbol);
      
      // Update asset settings
      assetConfig.settings = {
        ...assetConfig.settings,
        ...settings
      };
      assetConfig.lastUpdated = new Date().toISOString();
      
      aiTradingData.lastUpdated = new Date().toISOString();
      
      // Update the user document
      await updateDoc(docRef, {
        [`${firestoreAccountType}AccountData.aiTradingData`]: aiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      });
      
      // console.log(`✅ Settings updated for ${symbol}`);
    } catch (error) {
      console.error(`❌ Error updating settings for ${symbol}:`, error);
      throw error;
    }
  }

  /**
   * MIGRATION HELPER: Fix assets that have active strategies but enabled=false
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   */
  static async fixAssetEnabledStates(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<void> {
    try {
      // console.log(`🔧 Starting migration fix for ${accountType} account...`);
      
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        // console.log(`❌ User document not found`);
        return;
      }
      
      const userData = docSnap.data();
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      let fixedCount = 0;
      let structureFixed = false;
      const activeAssets: string[] = [];
      
      // Check each asset for active strategies with enabled=false
      if (aiTradingData.assets && typeof aiTradingData.assets === 'object') {
        Object.entries(aiTradingData.assets).forEach(([symbol, assetConfig]: [string, any]) => {
          // If asset has active strategy but is not enabled, fix it
          if (!assetConfig.enabled && 
              assetConfig.activeStrategyInstance &&
              assetConfig.activeStrategyInstance.isActive &&
              assetConfig.activeStrategyInstance.aiTradeStartEnabled) {
            
            // console.log(`🔄 Fixing ${symbol}: setting enabled=true`);
            assetConfig.enabled = true;
            assetConfig.lastUpdated = new Date().toISOString();
            fixedCount++;
          }
          
          // Collect all enabled assets
          if (assetConfig.enabled) {
            activeAssets.push(symbol);
          }
        });
      }
      
      // CRITICAL FIX: Ensure globalEnabled is true if any assets are enabled
      if (activeAssets.length > 0 && !aiTradingData.globalEnabled) {
        // console.log(`🔧 CRITICAL: Setting globalEnabled=true (${activeAssets.length} active assets: ${activeAssets.join(', ')})`);
        aiTradingData.globalEnabled = true;
        structureFixed = true;
      } else if (activeAssets.length === 0 && aiTradingData.globalEnabled) {
        // console.log(`🔧 Setting globalEnabled=false (no active assets)`);
        aiTradingData.globalEnabled = false;
        structureFixed = true;
      }
      
      // CRITICAL FIX: Ensure globalPerformance.activeAssets exists and is correct
      if (!aiTradingData.globalPerformance) {
        aiTradingData.globalPerformance = {
          totalTrades: 0,
          winningTrades: 0,
          losingTrades: 0,
          winRate: 0,
          totalPnL: 0,
          maxDrawdown: 0,
          sharpeRatio: 0,
          averageTradeDuration: 0,
          activeAssets: []
        };
        structureFixed = true;
      }
      
      if (!aiTradingData.globalPerformance.activeAssets) {
        aiTradingData.globalPerformance.activeAssets = [];
        structureFixed = true;
      }
      
      // Update activeAssets array if it's different
      const currentActiveAssets = aiTradingData.globalPerformance.activeAssets || [];
      if (JSON.stringify(currentActiveAssets.sort()) !== JSON.stringify(activeAssets.sort())) {
        // console.log(`🔧 CRITICAL: Updating activeAssets: [${activeAssets.join(', ')}]`);
        aiTradingData.globalPerformance.activeAssets = activeAssets;
        structureFixed = true;
      }
      
      if (fixedCount > 0 || structureFixed) {
        aiTradingData.lastUpdated = new Date().toISOString();
        
        const message = fixedCount > 0 && structureFixed ? 
          `${fixedCount} assets + critical structure` : 
          fixedCount > 0 ? `${fixedCount} assets` : 'critical structure';
          
        // console.log(`🔄 Migration fix: ${message}`);
        // console.log(`📊 Final state: globalEnabled=${aiTradingData.globalEnabled}, activeAssets=[${activeAssets.join(', ')}]`);
        
        // Update the user document
        await updateDoc(docRef, {
          [`${firestoreAccountType}AccountData.aiTradingData`]: aiTradingData,
          [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
        });
        
        // console.log(`✅ Migration fix completed successfully`);
      } else {
        // console.log(`✅ Migration check completed: no fixes needed`);
      }
      
    } catch (error) {
      console.error(`❌ Error in migration fix:`, error);
      throw error;
    }
  }

  /**
   * COMPLETE REMOVAL: Remove strategy completely from activeStrategies array
   * This method completely removes the strategy from Firestore activeStrategies array
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live') 
   * @param symbol - Trading symbol (e.g., 'ETHUSDT')
   */
  static async removeStrategyCompletely(
    userId: string,
    accountType: 'demo' | 'live',
    symbol: string
  ): Promise<void> {
    // console.log(`🗑️ COMPLETE REMOVAL initiated for ${symbol}...`);
    
    try {
      const docRef = doc(db, this.COLLECTION_NAME, userId);
      
      // Get existing user document
      const docSnap = await getDoc(docRef);
      const userData = docSnap.exists() ? docSnap.data() : {};
      
      // Map account type for Firestore
      const firestoreAccountType = accountType === 'live' ? 'real' : 'demo';
      
      // Ensure aiTradingData structure exists
      const aiTradingData = this.ensureAiTradingDataStructure(userData, firestoreAccountType);
      
      // 🔥 COMPLETELY REMOVE STRATEGY FROM activeStrategies ARRAY
      // console.log(`🔧 Removing strategy completely for ${symbol}:`);
      
      // Remove from activeStrategies array
      const activeStrategies = aiTradingData.activeStrategies || [];
      const strategyIndex = activeStrategies.findIndex((strategy: any) => 
        strategy.symbol === symbol
      );
      
      if (strategyIndex !== -1) {
        // First, explicitly set all strategy flags to FALSE before removing
        const strategyToRemove = activeStrategies[strategyIndex];
        strategyToRemove.aiTradeStartEnabled = false;  // ✅ A strategy is saved = FALSE
        strategyToRemove.isActive = false;             // ✅ Strategy is not currently running = FALSE
        strategyToRemove.aiSelectedStrategy = '';     // ✅ Clear selected strategy
        strategyToRemove.lastModified = new Date().toISOString();
        
        const removedStrategy = activeStrategies.splice(strategyIndex, 1)[0];
        // console.log(`   ✅ Removed strategy from activeStrategies with ALL FLAGS SET TO FALSE:`, removedStrategy);
      } else {
        // console.log(`   ⚠️ No strategy found in activeStrategies for ${symbol}`);
      }
      
      // Clear asset configuration completely - SET ALL TO FALSE
      const assetConfig = this.ensureAssetConfig(aiTradingData, symbol);
      assetConfig.enabled = false;                    // ✅ Asset has AI trading enabled = FALSE
      assetConfig.activeStrategyInstance = null;     // ✅ Strategy is properly configured = FALSE
      assetConfig.strategies = [];                   // ✅ Clear all strategies
      assetConfig.availableStrategies = [];          // ✅ Clear available strategies array
      assetConfig.lastUpdated = new Date().toISOString();
      // console.log(`   ✅ Cleared asset configuration for ${symbol} - ALL VALUES SET TO FALSE`);
      
      // Also clear legacy strategy configuration
      const legacyConfig = userData[`${firestoreAccountType}AccountData`]?.strategies?.[symbol];
      if (legacyConfig) {
        // Clear the legacy strategy
        const legacyStrategies = userData[`${firestoreAccountType}AccountData`].strategies;
        delete legacyStrategies[symbol];
        // console.log(`   ✅ Cleared legacy strategy configuration for ${symbol}`);
      }
      
      // Remove from global active assets list
      const globalActiveAssets = aiTradingData.globalPerformance.activeAssets || [];
      const assetIndex = globalActiveAssets.indexOf(symbol);
      if (assetIndex !== -1) {
        globalActiveAssets.splice(assetIndex, 1);
        //  console.log(`   ✅ Removed ${symbol} from global activeAssets: [${globalActiveAssets.join(', ')}]`);
      }
      
      // Update timestamps
      aiTradingData.lastUpdated = new Date().toISOString();
      
      // Save to Firestore
      const updateData: any = {
        [`${firestoreAccountType}AccountData.aiTradingData`]: aiTradingData,
        [`${firestoreAccountType}AccountData.lastUpdated`]: new Date().toISOString()
      };
      
      // Also update legacy strategies if they were cleared
      if (legacyConfig) {
        updateData[`${firestoreAccountType}AccountData.strategies`] = userData[`${firestoreAccountType}AccountData`].strategies;
      }
      
      await updateDoc(docRef, updateData);
      
      // console.log(`✅ COMPLETE REMOVAL successful for ${symbol}`);
    } catch (error) {
      console.error(`❌ Error during complete removal for ${symbol}:`, error);
      throw error;
    }
  }
}
